import type {
  PlayerProfileResponse,
  PublicPlayerDealResponsePagedData,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { PlayerNilDealsPage } from 'components/Nil/DealTracker/PlayerNilDealsPage';
import { AthleteProvider } from 'contexts/AthleteProvider';
import type { GetServerSideProps } from 'next';
import { useMemo } from 'react';

export interface IPlayerNILDealsPageProps {
  basicInfo: PlayerProfileResponse;
  playerDeals?: PublicPlayerDealResponsePagedData;
}

interface IPlayerNILDealsPage
  extends IGenericPageProps,
    IPlayerNILDealsPageProps {}

const PlayerNILDealsPage = ({
  playerDeals,
  basicInfo,
  ...props
}: IPlayerNILDealsPage) => {
  const data = useMemo(() => {
    return {
      athleteData: basicInfo,
    };
  }, [basicInfo]);

  const title = `${basicInfo?.name} - NIL Deals`;
  const description = `Latest NIL Deals for ${basicInfo?.name}. The On3 NIL Deal Tracker lists all NIL Deals across the NCAA for college and high school athletes being reported by players, collectives, agents and media. On3 does NOT disclose deal financial terms. Financial data is undisclosed and private to the athlete.`;
  const defaultAsset = basicInfo?.defaultAsset;
  const noIndex = playerDeals?.pagination?.count === 0;

  const image = useMemo(() => {
    const defaultImage = { height: 200, src: '', width: 200 };

    if (!defaultAsset) {
      return defaultImage;
    }

    const { domain, source, height, width } = defaultAsset;
    const src = (domain && source && `https://${domain}${source}`) || '';

    return { height: height || 200, src, width: width || 200 };
  }, [defaultAsset]);

  return (
    <AthleteProvider data={data}>
      <PageWrapper
        {...props}
        description={description}
        image={image}
        noIndex={noIndex}
        ogType="athlete"
        title={title}
      >
        <DatabaseLayout>
          <PlayerNilDealsPage playerDeals={playerDeals} />
        </DatabaseLayout>
      </PageWrapper>
    </AthleteProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { id } = ctx.query;
  const playerId = id?.toString().split('-').pop();

  if (!playerId) {
    return {
      notFound: true,
    };
  }

  let data = {};

  try {
    const [player] = await Promise.allSettled([
      proxyApi
        .get<PlayerProfileResponse>(`/public/rdb/v1/player/${playerId}/profile`)
        .then((r) => r.data),
    ]);

    if (player.status !== 'fulfilled') {
      return {
        notFound: true,
      };
    }

    const fetchedPlayer = player.value;

    const [playerDeals] = await Promise.allSettled([
      proxyApi
        .get<PublicPlayerDealResponsePagedData>(
          `/public/v2/players/${playerId}/deals`,
        )
        .then((r) => r.data),
    ]);

    data = {
      pageType: 'database',
      siteData,
      userData: userData || [],
      basicInfo: fetchedPlayer,
      playerDeals: playerDeals.status === 'fulfilled' ? playerDeals.value : {},
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: {} };
  }
};

export default withValidUrl(PlayerNILDealsPage);
